import type { DmsExportState } from '@makemydeal/dr-dash-types';
import { Box } from '@interstate/components/Box';
import { ExclamationCircleIcon } from '@interstate/components/Icons';
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { formatExportDate, formatExportTime } from '../../utils/formatUtils';

type RowProps = { label: string; value: string };

const renderPopoverRow = ({ label, value }: RowProps) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline',
            gap: '8px',
            marginBottom: '6px',
            '&:last-of-type': { marginBottom: 0 }
        }}
    >
        <Typography
            variant="body-xs"
            tag="span"
            sx={{
                flex: 1,
                color: 'var(--color-on-surface-subdued, #6d6d6d)',
                whiteSpace: 'nowrap',
                fontWeight: 500,
                lineHeight: '130%'
            }}
        >
            {label}
        </Typography>
        <Typography
            variant="body-xs"
            tag="span"
            sx={{ flex: 1, color: 'var(--color-on-surface-default, #000000)', textAlign: 'right', lineHeight: '130%' }}
        >
            {value}
        </Typography>
    </Box>
);

type DmsExportPopoverContentProps = {
    exportState: DmsExportState;
};

const DmsExportPopoverContent = ({ exportState }: DmsExportPopoverContentProps) => {
    const { status, exportBy, exportedAt, system, identifier } = exportState;
    const isSuccess = status === 'success';
    const formattedDate = formatExportDate(exportedAt);
    const formattedTime = formatExportTime(exportedAt);
    const theme = useInterstateTheme();
    const errorColor = theme.tokens.SemColorOnSurfaceErrorDefault;

    return (
        <Box sx={{ width: '248px' }}>
            {renderPopoverRow({ label: 'DMS Export', value: exportBy })}
            {renderPopoverRow({ label: 'Date', value: formattedDate })}
            {renderPopoverRow({ label: 'Time', value: formattedTime })}
            {renderPopoverRow({ label: 'System', value: system })}
            {isSuccess && identifier && renderPopoverRow({ label: 'Identifier', value: identifier })}
            {!isSuccess && (
                <Box
                    sx={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(0, 0, 0, 0.20)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <ExclamationCircleIcon style={{ width: '10.27px', height: '10.27px', flexShrink: 0 }} color={errorColor} />
                        <Typography variant="body-xxs" tag="span" sx={{ color: 'var(--color-on-surface-default, #000000)' }}>
                            The export was unsuccessful. Try again.
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default DmsExportPopoverContent;
